import { AnalysisHistoryList } from "@/components/analysis/analysis-history-list";
import { EmailRouting } from "@/components/analysis/email-routing";
import { Mailbox } from "@/components/analysis/mailbox";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/agents/analyst/email` — admin-only email source. Tabs: Routing (sender→project
 * mapping), Mailbox (recent messages + run analysis by hand), History
 * (analysed emails). New suggestions land in the AI Suggestions queue.
 *
 * E2E behaviour contract lives in `e2e/analysis-email.spec.ts`.
 */

const TABS = ["routing", "mailbox", "history"] as const;
type Tab = (typeof TABS)[number];

export function EmailPage() {
  const { t } = useTranslation();
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("routing");

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="w-full space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{t("agentTabs.email.title")}</h2>
        <p className="text-muted-foreground mt-1">{t("agentTabs.email.intro")}</p>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`email-tab-${key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(`agentTabs.email.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === "routing" && <EmailRouting />}
      {tab === "mailbox" && <Mailbox />}
      {tab === "history" && (
        <AnalysisHistoryList source="email" emptyLabel={t("agentTabs.email.historyEmpty")} />
      )}
    </div>
  );
}
